// <pfs-popover>: Generic anchored popover-Wrapper. Port von mednet-app's
// `<mednet-popover>` (PFS-133). Tenant-agnostisch.
//
// Anchored die native HTML-Popover-API an einen Trigger-Button. Mehrere
// Buttons mit `popovertarget="<this.id>"` koennen sich denselben Popover
// teilen — der zuletzt geklickte definiert die Anker-Position.
//
// Attribute:
//   placement: "top" | "bottom" | "left" | "right" + optional "-start" / "-end"
//              (default: "bottom-end")
//   flip:      "true" | "false" (default: "true") — auto-flip wenn keine
//              Platz auf der bevorzugten Seite
//
// Pattern fuer Shadow-DOM-Triggers: popovertarget kann nicht durch
// Shadow-Boundaries. WCs im Shadow-DOM koennen stattdessen ein Custom-
// Event `pfs:popover-toggle` mit detail.targetId an document.body
// dispatchen — pfs-popover hoert darauf.
//
// CSS-Vertrag: das Host-Element bekommt 5 Custom-Properties:
//   --popover-top, --popover-bottom, --popover-left, --popover-right,
//   --popover-transform
// Die CSS-Konsumenten lesen diese mit Fallbacks:
//   :host {
//     position: fixed;
//     top: var(--popover-top, 4rem);
//     right: var(--popover-right, 1rem);
//     transform: var(--popover-transform, none);
//   }

const ANCHOR_GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 8;
const TOGGLE_EVENT = "pfs:popover-toggle";

type PrimarySide = "top" | "bottom" | "left" | "right";
type Alignment = "start" | "end" | "center";

interface ParsedPlacement {
  side: PrimarySide;
  align: Alignment;
}

const VALID_SIDES: readonly PrimarySide[] = ["top", "bottom", "left", "right"];

class PfsPopover extends HTMLElement {
  private triggers: HTMLElement[] = [];
  // Native popover-API runs the "light dismiss" algorithm on mousedown when
  // the user clicks outside the popover body. That closes the popover BEFORE
  // the click-event listener on a trigger button fires. If we then call
  // togglePopover() in the click handler, we re-open what light-dismiss just
  // closed → popover never visually toggles. Snapshot at mousedown (before
  // light-dismiss runs) so the click handler knows whether we're in a
  // "already-closed-by-light-dismiss" state.
  private wasOpenAtMousedown = false;
  private readonly handleDocumentMousedown = () => {
    this.wasOpenAtMousedown = this.isOpen();
  };
  private readonly handleTriggerClick = (event: Event) => {
    const btn = event.currentTarget as HTMLElement;
    this.anchorAndToggle(btn);
  };
  private readonly handleShadowToggle = (event: Event) => {
    const ce = event as CustomEvent<{ targetId: string; rect?: DOMRect }>;
    if (!ce.detail || ce.detail.targetId !== this.id) return;
    if (this.wasOpenAtMousedown) {
      // Light-dismiss already closed the popover during the same mousedown.
      // Do nothing — anything else would re-open it.
      return;
    }
    if (ce.detail.rect) {
      const placement = this.resolvePlacement(ce.detail.rect);
      this.applyPlacement(placement, ce.detail.rect);
    }
    this.showIfHidden();
  };

  connectedCallback() {
    if (!this.id) {
      console.warn("pfs-popover requires an id attribute to match popovertarget buttons");
      return;
    }
    this.bindTriggers();
    document.body.addEventListener(TOGGLE_EVENT, this.handleShadowToggle);
    // capture-phase so we run BEFORE light-dismiss observes the mousedown.
    document.addEventListener("mousedown", this.handleDocumentMousedown, true);
  }

  disconnectedCallback() {
    this.unbindTriggers();
    document.body.removeEventListener(TOGGLE_EVENT, this.handleShadowToggle);
    document.removeEventListener("mousedown", this.handleDocumentMousedown, true);
  }

  private isOpen(): boolean {
    try {
      return this.matches(":popover-open");
    } catch {
      return false;
    }
  }

  private showIfHidden() {
    if (this.isOpen()) return;
    if ("showPopover" in this) {
      (this as HTMLElement & { showPopover?: () => void }).showPopover?.();
    }
  }

  private anchorAndToggle(trigger: HTMLElement) {
    if (this.wasOpenAtMousedown) return;
    this.anchorTo(trigger);
    this.showIfHidden();
  }

  private bindTriggers() {
    const root = this.getRootNode() as Document | ShadowRoot;
    this.triggers = Array.from(
      root.querySelectorAll<HTMLElement>(`[popovertarget="${this.id}"]`),
    );
    this.triggers.forEach((btn) => btn.addEventListener("click", this.handleTriggerClick));
  }

  private unbindTriggers() {
    this.triggers.forEach((btn) => btn.removeEventListener("click", this.handleTriggerClick));
    this.triggers = [];
  }

  private anchorTo(trigger: HTMLElement) {
    const rect = trigger.getBoundingClientRect();
    const placement = this.resolvePlacement(rect);
    this.applyPlacement(placement, rect);
  }

  private parsePlacement(raw: string): ParsedPlacement {
    const parts = raw.split("-");
    const side = parts[0] as PrimarySide;
    if (!VALID_SIDES.includes(side)) {
      console.warn(`pfs-popover: invalid placement "${raw}", falling back to bottom-end`);
      return { side: "bottom", align: "end" };
    }
    const alignPart = parts[1];
    const align: Alignment =
      alignPart === "start" || alignPart === "end" ? alignPart : "center";
    return { side, align };
  }

  private resolvePlacement(rect: DOMRect): ParsedPlacement {
    const preferred = this.parsePlacement(this.getAttribute("placement") ?? "bottom-end");
    const flipEnabled = this.getAttribute("flip") !== "false";
    if (!flipEnabled) return preferred;

    let { side, align } = preferred;

    if (!this.sideHasSpace(side, rect)) {
      const opp = this.oppositeSide(side);
      if (this.sideHasSpace(opp, rect)) side = opp;
    }

    if (align !== "center" && !this.alignmentFits(side, align, rect)) {
      const oppAlign: Alignment = align === "start" ? "end" : "start";
      if (this.alignmentFits(side, oppAlign, rect)) align = oppAlign;
    }

    return { side, align };
  }

  private oppositeSide(side: PrimarySide): PrimarySide {
    switch (side) {
      case "top":
        return "bottom";
      case "bottom":
        return "top";
      case "left":
        return "right";
      case "right":
        return "left";
    }
  }

  private readDimension(prop: "maxWidth" | "maxHeight"): number | null {
    const raw = window.getComputedStyle(this)[prop];
    if (!raw || raw === "none") return null;
    const px = parseFloat(raw);
    return Number.isFinite(px) && px > 0 ? px : null;
  }

  private sideHasSpace(side: PrimarySide, rect: DOMRect): boolean {
    if (side === "top" || side === "bottom") {
      const h = this.readDimension("maxHeight");
      if (h === null) return true;
      const need = h + ANCHOR_GAP_PX + VIEWPORT_MARGIN_PX;
      return side === "top"
        ? rect.top >= need
        : window.innerHeight - rect.bottom >= need;
    }
    const w = this.readDimension("maxWidth");
    if (w === null) return true;
    const need = w + ANCHOR_GAP_PX + VIEWPORT_MARGIN_PX;
    return side === "left"
      ? rect.left >= need
      : window.innerWidth - rect.right >= need;
  }

  private alignmentFits(side: PrimarySide, align: Alignment, rect: DOMRect): boolean {
    if (side === "top" || side === "bottom") {
      const maxW = this.readDimension("maxWidth");
      if (maxW === null) return true;
      const w = Math.min(maxW, window.innerWidth - 2 * VIEWPORT_MARGIN_PX);
      if (align === "start")
        return rect.left + w <= window.innerWidth - VIEWPORT_MARGIN_PX;
      if (align === "end") return rect.right - w >= VIEWPORT_MARGIN_PX;
      const c = rect.left + rect.width / 2;
      return (
        c - w / 2 >= VIEWPORT_MARGIN_PX &&
        c + w / 2 <= window.innerWidth - VIEWPORT_MARGIN_PX
      );
    }
    const maxH = this.readDimension("maxHeight");
    if (maxH === null) return true;
    if (align === "start")
      return rect.top + maxH <= window.innerHeight - VIEWPORT_MARGIN_PX;
    if (align === "end") return rect.bottom - maxH >= VIEWPORT_MARGIN_PX;
    const c = rect.top + rect.height / 2;
    return (
      c - maxH / 2 >= VIEWPORT_MARGIN_PX &&
      c + maxH / 2 <= window.innerHeight - VIEWPORT_MARGIN_PX
    );
  }

  private applyPlacement({ side, align }: ParsedPlacement, rect: DOMRect) {
    const reset = ["top", "bottom", "left", "right"] as const;
    reset.forEach((n) => this.style.setProperty(`--popover-${n}`, "auto"));
    this.style.setProperty("--popover-transform", "none");

    if (side === "top" || side === "bottom") {
      this.applyVerticalSide(side, rect);
      this.applyHorizontalAlignment(align, rect);
    } else {
      this.applyHorizontalSide(side, rect);
      this.applyVerticalAlignment(align, rect);
    }
  }

  private applyVerticalSide(side: "top" | "bottom", rect: DOMRect) {
    if (side === "bottom") {
      this.style.setProperty("--popover-top", `${rect.bottom + ANCHOR_GAP_PX}px`);
    } else {
      this.style.setProperty(
        "--popover-bottom",
        `${window.innerHeight - rect.top + ANCHOR_GAP_PX}px`,
      );
    }
  }

  private applyHorizontalSide(side: "left" | "right", rect: DOMRect) {
    if (side === "right") {
      this.style.setProperty("--popover-left", `${rect.right + ANCHOR_GAP_PX}px`);
    } else {
      this.style.setProperty(
        "--popover-right",
        `${window.innerWidth - rect.left + ANCHOR_GAP_PX}px`,
      );
    }
  }

  private applyHorizontalAlignment(align: Alignment, rect: DOMRect) {
    const maxW = this.readDimension("maxWidth");
    const w =
      maxW === null
        ? null
        : Math.min(maxW, window.innerWidth - 2 * VIEWPORT_MARGIN_PX);
    if (align === "start") {
      const raw = rect.left;
      const px =
        w === null
          ? raw
          : Math.max(
              VIEWPORT_MARGIN_PX,
              Math.min(raw, window.innerWidth - w - VIEWPORT_MARGIN_PX),
            );
      this.style.setProperty("--popover-left", `${px}px`);
    } else if (align === "end") {
      const raw = window.innerWidth - rect.right;
      const px =
        w === null
          ? raw
          : Math.max(
              VIEWPORT_MARGIN_PX,
              Math.min(raw, window.innerWidth - w - VIEWPORT_MARGIN_PX),
            );
      this.style.setProperty("--popover-right", `${px}px`);
    } else {
      const center = rect.left + rect.width / 2;
      const px =
        w === null
          ? center
          : Math.max(
              VIEWPORT_MARGIN_PX + w / 2,
              Math.min(center, window.innerWidth - VIEWPORT_MARGIN_PX - w / 2),
            );
      this.style.setProperty("--popover-left", `${px}px`);
      this.style.setProperty("--popover-transform", "translateX(-50%)");
    }
  }

  private applyVerticalAlignment(align: Alignment, rect: DOMRect) {
    const h = this.readDimension("maxHeight");
    if (align === "start") {
      const raw = rect.top;
      const px =
        h === null
          ? raw
          : Math.max(
              VIEWPORT_MARGIN_PX,
              Math.min(raw, window.innerHeight - h - VIEWPORT_MARGIN_PX),
            );
      this.style.setProperty("--popover-top", `${px}px`);
    } else if (align === "end") {
      const raw = window.innerHeight - rect.bottom;
      const px =
        h === null
          ? raw
          : Math.max(
              VIEWPORT_MARGIN_PX,
              Math.min(raw, window.innerHeight - h - VIEWPORT_MARGIN_PX),
            );
      this.style.setProperty("--popover-bottom", `${px}px`);
    } else {
      const center = rect.top + rect.height / 2;
      const px =
        h === null
          ? center
          : Math.max(
              VIEWPORT_MARGIN_PX + h / 2,
              Math.min(center, window.innerHeight - VIEWPORT_MARGIN_PX - h / 2),
            );
      this.style.setProperty("--popover-top", `${px}px`);
      this.style.setProperty("--popover-transform", "translateY(-50%)");
    }
  }
}

if (!customElements.get("pfs-popover")) {
  customElements.define("pfs-popover", PfsPopover);
}

export default PfsPopover;
